/* Privacy Policy — PaisaCX.

   Original draft prepared as a baseline document for an FIU-IND registered
   Indian cryptocurrency exchange. Aligned with India's Digital Personal Data
   Protection Act, 2023 (DPDPA) terminology and standard data-handling
   disclosures. Should be reviewed by qualified counsel before being treated
   as the binding legal text.
*/
import { Link } from "react-router-dom";
import LegalLayout from "@/components/legal/LegalLayout";

const sections = [
  {
    id: "scope",
    heading: "Scope of this Policy",
    body: (
      <>
        <p>
          This Privacy Policy describes how <strong>PaisaCX</strong> (&ldquo;<strong>PaisaCX</strong>&rdquo;,
          &ldquo;<strong>we</strong>&rdquo;, &ldquo;<strong>us</strong>&rdquo;) collects, uses,
          shares and protects the personal data of users (&ldquo;<strong>you</strong>&rdquo;) of
          our website and APIs (collectively, the &ldquo;
          <strong>Platform</strong>&rdquo;).
        </p>
        <p>
          By accessing the Platform you consent to the practices described below. This Policy
          should be read together with our <Link to="/terms">Terms of Service</Link> and{" "}
          <Link to="/aml-policy">AML &amp; KYC Policy</Link>.
        </p>
      </>
    ),
  },
  {
    id: "data-we-collect",
    heading: "Data We Collect",
    body: (
      <>
        <p>We collect the following categories of personal data:</p>
        <ul>
          <li>
            <strong>Identity &amp; KYC data:</strong> full name, date of birth, gender,
            nationality, photograph, PAN, Aadhaar (masked), passport, driving licence, video
            liveness selfie, and address proofs.
          </li>
          <li>
            <strong>Contact data:</strong> email address, mobile number, postal address.
          </li>
          <li>
            <strong>Financial &amp; transactional data:</strong> bank account details, UPI VPA,
            beneficial-ownership disclosures, source-of-funds declarations, deposit / withdrawal
            history, trade and order history, wallet addresses associated with your account.
          </li>
          <li>
            <strong>Device &amp; technical data:</strong> IP address, browser type, device
            identifiers, operating system, time-zone, log files and approximate geolocation.
          </li>
          <li>
            <strong>Usage data:</strong> pages visited, features used, session duration, referral
            source, in-app interactions and customer-support communications.
          </li>
          <li>
            <strong>Risk &amp; compliance data:</strong> sanctions, PEP and adverse-media
            screening results, AML risk scores, behavioural-analytics signals.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "purpose",
    heading: "How & Why We Use Your Data",
    body: (
      <>
        <p>We process your personal data for the following purposes:</p>
        <ul>
          <li>to verify your identity, perform KYC, and create / maintain your account;</li>
          <li>to facilitate trades, transfers, deposits and withdrawals on the Platform;</li>
          <li>to comply with applicable laws, including PMLA, FIU-IND requirements, GST and
              income-tax obligations;</li>
          <li>to detect, prevent and investigate fraud, market abuse, money laundering, terror
              financing, and sanctions violations;</li>
          <li>to operate, secure and continuously improve the Platform, including monitoring
              uptime and analysing performance;</li>
          <li>to provide customer support and respond to your queries or grievances;</li>
          <li>to send you product, service and security communications, and (where you have
              consented) marketing or promotional content;</li>
          <li>to enforce our Terms of Service and protect our legal rights.</li>
        </ul>
      </>
    ),
  },
  {
    id: "lawful-basis",
    heading: "Lawful Basis for Processing",
    body: (
      <p>
        We process your personal data on one or more of the following legal grounds: (i) the
        consent you provide at sign-up and at specific consent prompts; (ii) performance of the
        contract between you and PaisaCX; (iii) compliance with legal obligations to which we are
        subject (including FIU-IND, PMLA, RBI directions, and tax law); (iv) our legitimate
        interests in operating, improving and securing the Platform, balanced against your rights
        and freedoms.
      </p>
    ),
  },
  {
    id: "sharing",
    heading: "Data Sharing & Disclosures",
    body: (
      <>
        <p>We share your personal data only where necessary, with:</p>
        <ul>
          <li>
            <strong>Regulators &amp; authorities:</strong> FIU-IND, the Income-tax Department, RBI,
            law-enforcement agencies, and courts of competent jurisdiction.
          </li>
          <li>
            <strong>Service providers:</strong> KYC and identity-verification vendors, banking and
            payment partners, custody and blockchain analytics providers, cloud hosting
            providers, customer-support tooling, fraud-prevention engines.
          </li>
          <li>
            <strong>Auditors &amp; advisors:</strong> external statutory auditors, internal
            auditors and legal counsel, under confidentiality obligations.
          </li>
          <li>
            <strong>Corporate transactions:</strong> if we are involved in a merger, acquisition,
            financing, restructuring or sale of assets, your data may be transferred to the
            relevant counterparties subject to equivalent protections.
          </li>
        </ul>
        <p>
          We do <strong>not</strong> sell your personal data, and we do not share it with third
          parties for their independent marketing purposes.
        </p>
      </>
    ),
  },
  {
    id: "retention",
    heading: "Data Retention",
    body: (
      <p>
        We retain your personal data for as long as your account remains active, and thereafter
        for the period required by applicable law — typically a minimum of five (5) years from the
        date of the relevant transaction or account closure, in line with PMLA record-keeping
        requirements. Where retention is no longer required, we securely delete or anonymise the
        data.
      </p>
    ),
  },
  {
    id: "security",
    heading: "Security Measures",
    body: (
      <>
        <p>
          We follow industry-standard practices to protect your personal data, including:
        </p>
        <ul>
          <li>encryption-in-transit (TLS 1.2+) and encryption-at-rest for sensitive fields;</li>
          <li>segregation of customer assets from operating accounts, with the majority of
              digital assets held in cold storage with multi-party signing controls;</li>
          <li>two-factor authentication (2FA), device fingerprinting and anomaly detection;</li>
          <li>least-privilege access controls, audit logging, and quarterly access reviews;</li>
          <li>secure software-development lifecycle, peer code review and periodic penetration
              testing.</li>
        </ul>
        <p>
          No system is perfectly secure; in the event of a personal-data breach affecting you, we
          will notify you and the appropriate authorities in accordance with applicable law.
        </p>
      </>
    ),
  },
  {
    id: "your-rights",
    heading: "Your Rights",
    body: (
      <>
        <p>
          Subject to applicable law (including the Digital Personal Data Protection Act, 2023),
          you have the right to:
        </p>
        <ul>
          <li>access a summary of the personal data we hold about you;</li>
          <li>request correction or completion of inaccurate or incomplete data;</li>
          <li>request erasure of personal data we no longer need to retain;</li>
          <li>withdraw your consent for any processing that relies on consent;</li>
          <li>nominate another individual to exercise these rights on your behalf in the event
              of your incapacity or death;</li>
          <li>file a grievance with our <a href="#grievance">Grievance Officer</a> and, if not resolved,
              with the Data Protection Board of India.</li>
        </ul>
        <p>
          Some rights are not absolute — for example, we may need to retain certain records to
          comply with our PMLA obligations even after you request erasure.
        </p>
      </>
    ),
  },
  {
    id: "cookies",
    heading: "Cookies & Tracking Technologies",
    body: (
      <>
        <p>
          We use cookies and similar technologies to keep you signed in, remember your preferences,
          measure Platform usage, and protect against fraud. You can control cookies through your
          browser settings; disabling certain cookies may affect Platform functionality.
        </p>
        <p>
          We use a small number of analytics and security providers (such as anti-bot and
          web-analytics tools). These providers act as our processors and are bound by
          confidentiality obligations.
        </p>
      </>
    ),
  },
  {
    id: "international",
    heading: "International Transfers",
    body: (
      <p>
        Some of our service providers may process data on infrastructure located outside India.
        Where this is the case, we ensure that transfers comply with applicable Indian law and
        that the receiving party is bound by appropriate confidentiality and data-protection
        commitments. Where required, we will obtain your consent for such transfers.
      </p>
    ),
  },
  {
    id: "children",
    heading: "Children",
    body: (
      <p>
        The Platform is not directed to children under 18 years of age. We do not knowingly
        collect personal data from children. If you become aware that a child has provided us with
        personal data, please contact us so we can take appropriate steps to delete it.
      </p>
    ),
  },
  {
    id: "grievance",
    heading: "Grievance Officer",
    body: (
      <>
        <p>
          In accordance with applicable Indian law, our designated Grievance Officer can be
          reached at:
        </p>
        <ul>
          <li><strong>Email:</strong> support@paisacx.io</li>
          <li><strong>Contact form:</strong> paisacx.io/contact</li>
        </ul>
        <p>
          We aim to acknowledge complaints within 48 hours and resolve them within thirty (30)
          days of receipt.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    heading: "Changes to this Policy",
    body: (
      <p>
        We may update this Policy from time to time. Material changes will be communicated through
        the Platform and / or by email. The &ldquo;Last updated&rdquo; date at the top of this
        page indicates the latest revision.
      </p>
    ),
  },
];

export default function Privacy() {
  const intro = (
    <>
      In short: we collect only what we need to run a regulated crypto exchange — your identity
      documents (for KYC), your contact details, your trades, and basic device data. We use this
      to keep your account safe, comply with FIU-IND and tax law, and improve the Platform. We
      <strong> never sell your data</strong>, and we share it only with regulators, our service
      providers, and where the law requires us to. You can ask to see, correct, or delete your
      data at any time — write to <strong>support@paisacx.io</strong>.
    </>
  );
  return (
    <LegalLayout
      testid="privacy-page"
      title="Privacy Policy"
      description="How PaisaCX collects, uses, shares and protects your personal data, and the rights you have under Indian data-protection law."
      lastUpdated="30 April 2026"
      intro={intro}
      sections={sections}
    />
  );
}
